import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Generate or retrieve a session ID for this browser tab
function getSessionId(): string {
  let sid = sessionStorage.getItem("_sid");
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("_sid", sid);
  }
  return sid;
}

// Extract browser family only (e.g. "Chrome", "Firefox") — avoids fingerprinting
function getBrowserFamily(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("OPR/") || ua.includes("Opera")) return "Opera";
  if (ua.includes("Chrome") && !ua.includes("Edg/")) return "Chrome";
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  return "Other";
}

// Strip query parameters and fragments from referrer to prevent PII leakage
function sanitizeReferrer(ref: string): string | null {
  if (!ref) return null;
  try {
    const url = new URL(ref);
    return url.origin + url.pathname;
  } catch {
    return null;
  }
}

export function usePageTracking() {
  const location = useLocation();
  const lastTracked = useRef<string>("");

  useEffect(() => {
    const path = location.pathname;
    if (path === lastTracked.current) return;
    lastTracked.current = path;

    (async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data: { user } } = await supabase.auth.getUser();
        await supabase.from("page_views").insert({
          path,
          user_id: user?.id ?? null,
          session_id: getSessionId(),
          referrer: sanitizeReferrer(document.referrer),
          user_agent: getBrowserFamily(),
        });
      } catch {
        // Silently ignore tracking errors — never block the user
      }
    })();
  }, [location.pathname]);
}
